import {
  Badge,
  Box,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import AddAntecedentesAlergiaModal from 'components/layouts/patients/AddAntecedentesAlergiaModal';
import React from 'react';
import { MdAdd, MdMedicalServices } from 'react-icons/md';
import {
  AntecedentesCompletos,
  Genero,
  Patient,
} from 'types/models';

interface Group {
  category: string;
  colorScheme: string;
  items: string[];
}

function buildGroups(
  resumen: AntecedentesCompletos | undefined,
  patient: Patient,
): Group[] {
  const groups: Group[] = [];

  const hf = resumen?.heredofamiliares;
  if (hf) {
    const items: string[] = [];
    if (hf.diabetes) items.push('Diabetes');
    if (hf.hipertension) items.push('Hipertensión');
    if (hf.cardiopatias) items.push('Cardiopatías');
    if (hf.cancer) items.push('Cáncer');
    if (hf.enfermedadesRespiratorias) items.push('Enf. Respiratorias');
    if (hf.enfermedadesRenales) items.push('Enf. Renales');
    if (hf.enfermedadesNeurologicas) items.push('Enf. Neurológicas');
    if (hf.enfermedadesMentales) items.push('Enf. Mentales');
    if (items.length) groups.push({ category: 'Heredo-familiares', colorScheme: 'blue', items });
  }

  const pat = resumen?.patologicos;
  if (pat) {
    const items: string[] = [];
    if (pat.diabetesMellitus) items.push('Diabetes Mellitus');
    if (pat.hipertensionArterial) items.push('HTA');
    if (pat.cardiopatias) items.push('Cardiopatías');
    if ((pat as any).cancer) items.push('Cáncer');
    if (pat.tuberculosis) items.push('Tuberculosis');
    if (pat.hepatitis) items.push('Hepatitis');
    if (pat.vihSida) items.push('VIH/SIDA');
    if (pat.covid19) items.push('COVID-19');
    if (pat.epilepsia) items.push('Epilepsia');
    if (pat.accidenteCerebrovascular) items.push('ACV');
    if (pat.depresion) items.push('Depresión');
    if (pat.ansiedad) items.push('Ansiedad');
    if (pat.transfusiones) items.push('Transfusiones');
    if (items.length) groups.push({ category: 'Patológicos', colorScheme: 'red', items });
  }

  const np = resumen?.noPatologicos;
  if (np) {
    const items: string[] = [];
    if (np.tabaquismo) items.push('Tabaquismo');
    if (np.alcoholismo) items.push('Alcoholismo');
    if (np.drogas) items.push('Drogas');
    if (np.cafe) items.push('Café');
    if (np.actividadFisica) items.push('Act. Física');
    if (items.length) groups.push({ category: 'No patológicos', colorScheme: 'green', items });
  }

  if (patient.genero === Genero.FEMENINO) {
    const g = resumen?.ginecoObstetricos;
    if (g) {
      const items: string[] = [];
      if (g.embarazoActual) items.push('Embarazo actual');
      if (g.menopausia) items.push('Menopausia');
      if (g.dismenorrea) items.push('Dismenorrea');
      if (g.gestas > 0)
        items.push(`G${g.gestas}P${g.partos}C${g.cesareas}A${g.abortos}`);
      if (items.length)
        groups.push({ category: 'Gineco-obstétricos', colorScheme: 'pink', items });
    }
  }

  return groups;
}

interface AntecedentesCompactPanelProps {
  patient: Patient;
  resumen: AntecedentesCompletos | undefined;
  [x: string]: any;
}

export default function AntecedentesCompactPanel({
  patient,
  resumen,
  ...rest
}: AntecedentesCompactPanelProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const emptyBg = useColorModeValue('gray.50', 'navy.700');

  const groups = buildGroups(resumen, patient);
  const hasAny = groups.length > 0;

  return (
    <>
      <Card p='20px' {...rest}>
        <Flex align='center' justify='space-between' mb='14px'>
          <Text color={textColor} fontSize='sm' fontWeight='800'>
            Antecedentes Clínicos
          </Text>
          <Tooltip label='Agregar antecedente' placement='top' hasArrow>
            <IconButton
              aria-label='Agregar antecedente'
              icon={<Icon as={MdAdd} />}
              size='sm'
              variant='outline'
              colorScheme='brand'
              borderRadius='full'
              onClick={onOpen}
            />
          </Tooltip>
        </Flex>

        {!hasAny ? (
          <Flex
            direction='column'
            align='center'
            justify='center'
            gap='8px'
            py='32px'
            bg={emptyBg}
            borderRadius='12px'>
            <Icon as={MdMedicalServices} color='secondaryGray.300' w='28px' h='28px' />
            <Text fontSize='sm' color={mutedColor} fontWeight='600'>
              Sin antecedentes registrados
            </Text>
            <Text fontSize='xs' color={mutedColor}>
              Haz clic en "Agregar antecedente" para comenzar
            </Text>
          </Flex>
        ) : (
          <Flex direction='column' gap='14px'>
            {groups.map((group) => (
              <Box key={group.category}>
                <Text
                  fontSize='10px'
                  fontWeight='700'
                  color={mutedColor}
                  textTransform='uppercase'
                  letterSpacing='0.08em'
                  mb='6px'>
                  {group.category}
                </Text>
                <Flex flexWrap='wrap' gap='6px'>
                  {group.items.map((item) => (
                    <Badge
                      key={item}
                      colorScheme={group.colorScheme}
                      borderRadius='full'
                      px='10px'
                      py='4px'
                      fontSize='xs'
                      fontWeight='600'>
                      {item}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            ))}
          </Flex>
        )}
      </Card>

      <AddAntecedentesAlergiaModal
        isOpen={isOpen}
        onClose={onClose}
        patientId={patient.id}
        initialTab={0}
      />
    </>
  );
}
